class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleVariantChanged.bind(this),
    );
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.updateBuyButtons.bind(this),
    );
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.variantChangeUnsubscriber?.();
    this.cartUpdateUnsubscriber?.();
  }

  submitHandler(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const items = [];
    const item = {};
    for (const [key, value] of formData.entries()) {
      item[key] = value;
    }
    item.product_id = this.dataset.productId;
    items.push(item);

    const data = {
      type: "add",
      source: "product-page",
      items,
      submitter: e.submitter,
    };
    updateCart(data);
  }

  clickHandler(e) {
    if (e.target.closest(".details-group__label")) {
      const accordions = e.target.closest(".details-group__accordions");
      const active_accordion = accordions.querySelector(
        `.accordion-control#${e.target.closest(".details-group__label").getAttribute("for")}`,
      );
      if (active_accordion.checked) {
        setTimeout(() => {
          active_accordion.checked = false;
        }, 10);
      }
    }

    if (e.target.closest("a[data-accordion-control]")) {
      const anchor = e.target.closest("a[data-accordion-control]");
      const target = document.querySelector(anchor.getAttribute("href"));
      if (!target) return;
      e.preventDefault();

      const accordionTarget = anchor.dataset.accordionTarget
        ? document.querySelector(anchor.dataset.accordionTarget)
        : target;
      let radio;
      if (accordionTarget?.classList.contains("product-details__accordion")) {
        radio = accordionTarget.previousElementSibling;
      }
      if (radio) radio.checked = true;

      const headerEl =
        document.querySelector(".shopify-section-header-sticky") ||
        document.querySelector("header");
      const offset = headerEl ? headerEl.offsetHeight : 0;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - offset - 16,
        behavior: "smooth",
      });
    }
  }

  async renderNodes(target, selectedOptionValues) {
    const renderId = (this.variantRenderId || 0) + 1;
    this.variantRenderId = renderId;
    this.updateVariantLoadingState(true);

    try {
      // construct url with option values
      const productUrl = this.dataset.url;
      const url = this.getRequestUrl(productUrl, selectedOptionValues);
      const html = await this.fetchProductDOM(url);
      if (!html) return false;

      // replace specified DOM nodes
      const replacedNodes = [];
      for (const node of this.nodesToRender) {
        const target = node.id || node.selector;
        if (!target) continue;
        const original_node = this.querySelector(target);
        const new_node = html.querySelector(target);
        if (!original_node || !new_node) continue;
        original_node.parentNode.insertBefore(new_node, original_node);
        original_node.style.display = "none";
        replacedNodes.push(original_node);
      }

      this.updateVariantLoadingState(true);

      replacedNodes.forEach((node) => node.remove());
      return true;
    } finally {
      if (this.variantRenderId === renderId) {
        this.updateVariantLoadingState(false);
      }
    }
  }

  updateVariantLoadingState(isLoading) {
    this.classList.toggle("data-loading", isLoading);

    this.querySelectorAll('[data-product-form] button[type="submit"]').forEach((button) => {
      button.classList.toggle("btn--loading", isLoading);
      button.toggleAttribute("aria-busy", isLoading);

      if (isLoading && !button.disabled) {
        button.disabled = true;
        button.dataset.variantLoadingDisabled = "true";
      } else if (!isLoading && button.dataset.variantLoadingDisabled) {
        button.disabled = false;
        delete button.dataset.variantLoadingDisabled;
      }
    });
  }
  async handleVariantChanged({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    const hasRendered = await this.renderNodes(target, selectedOptionValues);
    if (!hasRendered) return;

    // re-focus changed input
    document.querySelector(`#${target.id}`)?.focus();

    const variant_json = this.querySelector("[data-variant-json]");
    if (!variant_json) return;

    const variant = JSON.parse(variant_json.innerHTML);
    this.querySelector("[data-product-form]")?.updateTotal?.();
    if (variant.featured_image) {
      this.updateVariantMedia(variant.featured_image);
    }

    this.updateHistory(variant.id);
  }

  async updateBuyButtons({ data }) {
    const { source, submitter, product_ids, response } = data;

    if (!product_ids.includes(this.dataset.productId) && response.items.length > 0) return;

    this.renderNodes(target, selectedOptionValues);
  }

  getRequestUrl(url, optionValues = []) {
    const params = [];

    params.push(`section_id=${this.dataset.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(",")}`);
    }

    return `${url}?${params.join("&")}`;
  }

  async fetchProductDOM(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      const data = await res.text();
      const html = new DOMParser().parseFromString(data, "text/html");
      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted by user");
      } else {
        console.error(error);
      }
    }
  }

  updateVariantMedia(image) {
    this.media_gallery = this.querySelector("media-gallery");
    if (!this.media_gallery) return;

    const gallery_slide = this.media_gallery.querySelector(`[data-media="${image}"]`).dataset
      .swiperSlideIndex;

    this.media_gallery.swiper.slideTo(parseInt(gallery_slide));
  }

  updateHistory(id) {
    if (this.hasAttribute("data-cart-edit")) return;
    if (!history.replaceState) return;
    const url = `${this.dataset.url}${id ? `?variant=${id}` : ""}`;
    window.history.replaceState({}, "", url);
  }

  get nodesToRender() {
    return [
      {
        selector: "variant-select",
      },
      {
        selector: "delivery-selector",
      },
      {
        selector: ".main-product__price",
      },
      {
        selector: ".main-product__sku",
      },
      {
        selector: ".product-form__master",
      },
      {
        selector: ".product-form__button",
      },
    ];
  }
}

customElements.define("main-product", MainProduct);

class ProductDetailsInteractions extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  clickHandler(e) {
    if (e.target.closest(".details-group__label")) {
      const accordions = e.target.closest(".details-group__accordions");
      const active_accordion = accordions.querySelector(
        `.accordion-control#${e.target.closest(".details-group__label").getAttribute("for")}`,
      );
      if (active_accordion.checked) {
        setTimeout(() => {
          active_accordion.checked = false;
        }, 10);
      }
    }

    if (e.target.closest("a[data-accordion-control]")) {
      const anchor = e.target.closest("a[data-accordion-control]");
      const target = document.querySelector(anchor.getAttribute("href"));
      if (!target) return;
      e.preventDefault();

      const accordionTarget = anchor.dataset.accordionTarget
        ? document.querySelector(anchor.dataset.accordionTarget)
        : target;
      let radio;
      if (accordionTarget?.classList.contains("product-details__accordion")) {
        radio = accordionTarget.previousElementSibling;
      }
      if (radio) radio.checked = true;

      const headerEl =
        document.querySelector(".shopify-section-header-sticky") ||
        document.querySelector("header");
      const offset = headerEl ? headerEl.offsetHeight : 0;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - offset - 16,
        behavior: "smooth",
      });
    }
  }
}

customElements.define("product-details-interactions", ProductDetailsInteractions);

class VariantSelect extends HTMLElement {
  constructor() {
    super();

    this.events = [
      {
        el: this,
        type: "input",
        callback: this.variantChanged.bind(this),
      },
    ];
  }

  connectedCallback() {
    const variant_json = this.querySelector("[data-variant-json]");
    this.variant_json = variant_json ? JSON.parse(variant_json.innerHTML) : null;
    handleEventListeners(this.events, "add");
    if (!this.querySelector('[data-option-title="delivery-type"]')) return;
    this.deliveryChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.deliveryOptionChange,
      this.handleDeliveryChanged.bind(this),
    );
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.deliveryChangeUnsubscriber?.();
  }

  variantChanged(e) {
    if (!this.variant_json) return;

    const values = this.selectedOptionValues;
    const target = e.target.tagName === "SELECT" ? e.target.selectedOptions[0] : e.target;

    // this.updateSelectedTitle(e);

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event: e, target, selectedOptionValues: values },
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }

  handleDeliveryChanged({ data }) {
    const delivery_option = this.querySelector(
      `[data-option-value-id="${data.selectedOptionValues[0]}"]`,
    );
    if (!delivery_option) return;
    delivery_option.checked = true;
    delivery_option.dispatchEvent(new Event("input", { bubbles: true }));
  }

  updateSelectedTitle({ target }) {
    const { value } = target;
    const selectedOptionTitle = target
      .closest(`.variant-select__group`)
      .querySelector("[data-selected-value]");
    if (selectedOptionTitle) selectedOptionTitle.innerHTML = value;
  }
}

customElements.define("variant-select", VariantSelect);

class DeliverySelector extends HTMLElement {
  constructor() {
    super();

    this.events = [
      {
        el: this,
        type: "input",
        callback: this.deliveryOptionChanged.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");

    this.deliveryDateChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.deliveryDateChange,
      this.updateDeliveryOptions.bind(this),
    );
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.deliveryDateChangeUnsubscriber?.();
  }

  deliveryOptionChanged(e) {
    const values = this.selectedOptionValues;
    const target = e.target.tagName === "SELECT" ? e.target.selectedOptions[0] : e.target;

    publish(PUB_SUB_EVENTS.deliveryOptionChange, {
      data: { event: e, target, selectedOptionValues: values },
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }

  updateDeliveryOptions({ data }) {
    this.selectOptionForDay(data.day);
  }

  selectOptionForDay(day) {
    const firstOption = this.querySelector(`[data-${day}] input`);
    if (!firstOption || firstOption.checked) return;

    firstOption.checked = true;
    firstOption.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

customElements.define("delivery-selector", DeliverySelector);

class DeliveryDatePicker extends HTMLElement {
  connectedCallback() {
    const config = this.getConfig();
    this.init(config);
    this.cutoffTimer = setInterval(() => this.refreshCutoffState(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.cutoffTimer);
    this.cutoffTimer = null;
    this.date_picker?.destroy();
    this.date_picker = null;
  }

  init(config) {
    const date_picker_el = this.querySelector(".delivery-date-picker__input");
    if (!date_picker_el) return;
    const delivery_date = this.querySelector("[data-delivery-date-value]")?.value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(delivery_date)) {
      const [year, month, day] = delivery_date.split("-").map(Number);
      config.defaultDate = new Date(year, month - 1, day);
    } else if (date_picker_el.value) {
      config.defaultDate = flatpickr.parseDate(date_picker_el.value, config.dateFormat);
    }
    if (this.closest("dialog")) config.static = true;
    this.date_picker = flatpickr(date_picker_el, config);
  }

  getConfig() {
    this.restricted_dates = new Set(
      (this.dataset.restrictedDates || "")
        .split(",")
        .map((date) => date.trim())
        .filter(Boolean),
    );

    const today = new Date();
    const cutoff = this.getCutoffDate(today);
    const current = {
      today,
      date: cutoff.getDate(),
      month: cutoff.getMonth(),
      year: cutoff.getFullYear(),
    };
    const first_available = this.getFirstAvailableDate(current);
    this.updateCutoffWindow(first_available);

    const disabled_dates = [
      (date) =>
        date.getDay() === 0 ||
        date.getDay() === 1 ||
        this.restricted_dates.has(this.formatDate(date)),
    ];
    const config = {
      defaultDate: first_available,
      dateFormat: "l d F Y",
      minDate: first_available,
      disable: disabled_dates,
      locale: {
        firstDayOfWeek: 1,
      },
      onReady: (dates, str, inst) => this.checkDateChange(dates, str, inst),
      onChange: (dates, str, inst) => this.checkDateChange(dates, str, inst),
    };
    return config;
  }

  getFirstAvailableDate(current) {
    let adjusted = new Date(current.year, current.month, current.date + 2); // 2 day lead
    while (
      adjusted.getDay() === 0 ||
      adjusted.getDay() === 1 ||
      this.restricted_dates.has(this.formatDate(adjusted))
    ) {
      adjusted = this.parseDate(adjusted, 1);
    }
    return adjusted;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  checkDateChange(dates, str, inst) {
    const date = dates[0];
    this.syncDeliveryDate(date, inst);

    if (!date) return;
    const data = {};
    if (date.getDay() === 6) {
      data.day = "weekend";
    } else {
      data.day = "weekday";
    }
    this.dataset.deliveryWindow = data.day;
    publish(PUB_SUB_EVENTS.deliveryDateChange, {
      data,
    });
  }

  syncDeliveryDate(date, inst) {
    const deliveryDateValueInput = this.querySelector("[data-delivery-date-value]");
    if (!deliveryDateValueInput) return;
    deliveryDateValueInput.value = date ? inst.formatDate(date, "Y-m-d") : "";
  }

  parseDate(date, offset = 0) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset);
  }

  getDailyCutoffDate(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 17, 30);
  }

  getCutoffDate(date) {
    const cutoff = this.getDailyCutoffDate(date);

    if (date >= cutoff) {
      cutoff.setDate(cutoff.getDate() + 1);
    }

    return cutoff;
  }

  refreshCutoffState() {
    const today = new Date();
    const cutoff = this.getCutoffDate(today);
    const firstAvailableDate = this.getFirstAvailableDate({
      today,
      date: cutoff.getDate(),
      month: cutoff.getMonth(),
      year: cutoff.getFullYear(),
    });

    this.updateCutoffWindow(firstAvailableDate);

    const minDate = this.date_picker?.config.minDate;
    if (!minDate || minDate.getTime() === firstAvailableDate.getTime()) return;

    this.date_picker.set("minDate", firstAvailableDate);
    const selectedDate = this.date_picker.selectedDates[0];
    if (!selectedDate || !this.date_picker.isEnabled(selectedDate)) {
      this.date_picker.setDate(firstAvailableDate, true);
    }
  }

  updateCutoffWindow(firstAvailableDate) {
    const cutoffDayEls = document.querySelectorAll("[data-window-cutoff-day]");
    const cutoffTimeEls = document.querySelectorAll("[data-window-cutoff-time]");
    const currentDate = new Date();
    const deliveryCutoffDate = this.getDeliveryCutoffDate(firstAvailableDate);

    for (const el of cutoffDayEls) {
      el.textContent = this.formatCutoffDay(firstAvailableDate);
    }

    for (const el of cutoffTimeEls) {
      el.textContent = this.formatCutoffTime(deliveryCutoffDate, currentDate);
    }
  }

  getDeliveryCutoffDate(firstAvailableDate) {
    return new Date(
      firstAvailableDate.getFullYear(),
      firstAvailableDate.getMonth(),
      firstAvailableDate.getDate() - 2,
      17,
      30,
    );
  }

  formatCutoffDay(date) {
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  }

  formatCutoffTime(cutoffDate, currentDate) {
    const diff = Math.max(0, cutoffDate.getTime() - currentDate.getTime());
    const totalMinutes = Math.ceil(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (days > 0) {
      parts.push(`${days} ${days === 1 ? "day" : "days"}`);
    }

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? "hr" : "hrs"}`);
    }

    parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`);

    return parts.join(" ");
  }
}

customElements.define("delivery-date-picker", DeliveryDatePicker);

class GiftingOptions extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest("form");
    this.optionInputs = Array.from(this.querySelectorAll("[data-gifting-option]"));
    this.events = [
      {
        el: this,
        type: "change",
        callback: this.handleChange.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
    this.submitButtonObserver = new MutationObserver(() => this.updateSubmitButton());
    if (this.form) {
      this.submitButtonObserver.observe(this.form, { childList: true, subtree: true });
    }
    this.initConfirmationDatePicker();
    this.update();
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.submitButtonObserver?.disconnect();
    this.submitButtonObserver = null;
    if (this.confirmationDatePicker) this.confirmationDatePicker.destroy();
    this.confirmationDatePicker = null;
  }

  initConfirmationDatePicker() {
    const confirmationDateInput = this.form?.querySelector("[data-gifting-date]");
    if (!confirmationDateInput || !window.flatpickr) return;

    const config = {
      dateFormat: "l d F Y",
      disableMobile: true,
      minDate: "today",
      locale: {
        firstDayOfWeek: 1,
      },
    };

    if (confirmationDateInput.hasAttribute("data-shopify-recipient-send-on")) {
      config.dateFormat = "Y-m-d";
      config.altInput = true;
      config.altFormat = "l d F Y";
      config.maxDate = new Date().fp_incr(90);
    }

    this.confirmationDatePicker = flatpickr(confirmationDateInput, config);

    const recipientOffsetInput = this.form?.querySelector("[data-shopify-recipient-offset]");
    if (recipientOffsetInput) recipientOffsetInput.value = new Date().getTimezoneOffset();
  }

  handleChange() {
    this.update();
  }

  update() {
    const deliveryDateInput = this.form?.querySelector(".delivery-date-picker__input");
    const fieldInputs = Array.from(this.form?.querySelectorAll("[data-gifting-field]") || []);
    const giftingEnabled = this.querySelector("[data-gifting-toggle]")?.checked;
    const recipientFieldsOnly = this.hasAttribute("data-recipient-fields-only");
    const messageOnly = this.hasAttribute("data-message-only");
    const selectedOption = this.optionInputs.find((input) => input.checked)?.dataset.giftingOption;
    const hasSelectedOption = recipientFieldsOnly || messageOnly || Boolean(selectedOption);
    const shouldShowDeliveryDate =
      messageOnly || !giftingEnabled || (!recipientFieldsOnly && selectedOption === "known-date");
    const shouldShowRecipientFields = giftingEnabled && hasSelectedOption;

    this.updateSubmitButton(giftingEnabled, hasSelectedOption);

    if (
      giftingEnabled &&
      !messageOnly &&
      (recipientFieldsOnly || selectedOption === "recipient-date")
    ) {
      this.selectGiftDeliveryOption();
    } else {
      this.selectDeliveryOptionForDate();
    }

    if (deliveryDateInput) this.setDateInputDisabled(deliveryDateInput, !shouldShowDeliveryDate);
    const deliveryDateValueInput = this.form?.querySelector("[data-delivery-date-value]");
    if (deliveryDateValueInput) deliveryDateValueInput.disabled = !shouldShowDeliveryDate;

    fieldInputs.forEach((input) => {
      this.setDateInputDisabled(input, !shouldShowRecipientFields);
    });

    if (!giftingEnabled) {
      this.optionInputs.forEach((input) => {
        input.checked = false;
      });
    }
  }

  selectGiftDeliveryOption() {
    const giftDeliveryOption = this.form?.querySelector("[data-gift-delivery-option]");
    if (!giftDeliveryOption || giftDeliveryOption.checked) return;

    giftDeliveryOption.checked = true;
    giftDeliveryOption.dispatchEvent(new Event("input", { bubbles: true }));
  }

  selectDeliveryOptionForDate() {
    const deliveryDatePicker = this.form?.querySelector("delivery-date-picker");
    const deliverySelector = this.form?.querySelector("delivery-selector");
    const deliveryWindow = deliveryDatePicker?.dataset.deliveryWindow;
    if (!deliveryWindow || !deliverySelector) return;

    deliverySelector.selectOptionForDay(deliveryWindow);
  }

  updateSubmitButton(
    giftingEnabled = this.querySelector("[data-gifting-toggle]")?.checked,
    hasSelectedOption = this.hasAttribute("data-recipient-fields-only") ||
      this.hasAttribute("data-message-only") ||
      this.optionInputs.some((input) => input.checked),
  ) {
    const submitButton = this.form?.querySelector('.product-form__button button[type="submit"]');
    if (!submitButton) return;

    const shouldDisable = giftingEnabled && !hasSelectedOption;
    if (shouldDisable && !submitButton.disabled) {
      submitButton.disabled = true;
      submitButton.dataset.giftingDisabled = "true";
      submitButton.classList.add("btn-style__nostock");
    } else if (!shouldDisable && submitButton.dataset.giftingDisabled) {
      submitButton.disabled = false;
      submitButton.classList.remove("btn-style__nostock");
      delete submitButton.dataset.giftingDisabled;
    }
  }

  setDateInputDisabled(input, disabled) {
    input.disabled = disabled;
    if (input._flatpickr?.altInput) {
      input._flatpickr.altInput.disabled = disabled;
    }
  }
}

customElements.define("gifting-options", GiftingOptions);

class DigitalGiftRecipientFields extends HTMLElement {
  connectedCallback() {
    const confirmationDateInput = this.querySelector("[data-gifting-date]");
    if (!confirmationDateInput || !window.flatpickr) return;

    this.confirmationDatePicker = flatpickr(confirmationDateInput, {
      dateFormat: "l d F Y",
      disableMobile: true,
      minDate: "today",
      locale: {
        firstDayOfWeek: 1,
      },
    });
  }

  disconnectedCallback() {
    if (this.confirmationDatePicker) this.confirmationDatePicker.destroy();
    this.confirmationDatePicker = null;
  }
}

customElements.define("digital-gift-recipient-fields", DigitalGiftRecipientFields);

class ProductModalDialog extends HTMLElement {
  constructor() {
    super();
    this.is_open = false;
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: document,
        type: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }
  connectedCallback() {
    handleEventListeners(this.events, "add");
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }
  clickHandler(e) {
    const action_target =
      e.target.closest("[modal-dialog-action]")?.getAttribute("modal-dialog-action") || null;
    if (action_target === "open") {
      e.preventDefault();
      this.openModal();
    } else if (action_target === "close" || !e.target.closest(".modal-main__inner")) {
      e.preventDefault();
      this.closeModal();
    }
  }
  keyboardHandler(e) {
    if ((e.key === "Escape" || e.keyCode === 27) && this.is_open === true) {
      this.closeModal();
    }
  }
  openModal() {
    this.is_open = true;
    this.setAttribute("aria-hidden", "false");
    this.setAttribute("modal-dialog-view", "visible");
  }
  closeModal() {
    this.is_open = false;
    this.setAttribute("aria-hidden", "true");
    this.setAttribute("modal-dialog-view", "hidden");
  }
}

customElements.define("product-modal-dialog", ProductModalDialog);

class MediaZoom extends ProductModalDialog {
  constructor() {
    super();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.eventCache = [];
    this.previousDiff = -1;
    this.base_events = this.events;
  }

  connectedCallback() {
    this.cartridge = this.querySelector("[data-zoom-image]");
    this.events = [...this.base_events];

    if (this.cartridge) {
      this.events.push(
        {
          el: this.cartridge,
          type: "wheel",
          callback: this.coarseHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "mousedown",
          callback: this.panStartHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "mouseup",
          callback: this.panEndHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "mouseleave",
          callback: this.panEndHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "mousemove",
          callback: this.panHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointerdown",
          callback: this.pointerDownHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointermove",
          callback: this.pointerMoveHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointerup",
          callback: this.pointerUpHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointercancel",
          callback: this.pointerUpHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointerout",
          callback: this.pointerUpHandler.bind(this),
        },
        {
          el: this.cartridge,
          type: "pointerleave",
          callback: this.pointerUpHandler.bind(this),
        },
      );
    }

    super.connectedCallback();
  }

  openModal() {
    super.openModal();
    this.cartridgeWidth = this.cartridge.offsetWidth;
    this.cartridgeHeight = this.cartridge.offsetHeight;

    this.loadImage();
  }

  closeModal() {
    super.closeModal();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge.removeAttribute("style");
  }

  clickHandler(e) {
    super.clickHandler(e);
  }

  setZoom(delta) {
    this.s -= delta;
    this.s = Math.min(Math.max(this.s, 100), 200);
    if (this.cartridgeWidth > this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `auto ${this.s}%`;
    } else if (this.cartridgeWidth < this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `${this.s}% auto`;
    } else {
      this.cartridge.style.backgroundSize = `${this.s}% ${this.s}%`;
    }
  }

  coarseHandler(e) {
    e.preventDefault();
    if (e.type === "wheel") {
      this.setZoom(e.deltaY);
    }
  }

  panStartHandler(e) {
    if (
      typeof this.y == "undefined" ||
      typeof this.x == "undefined" ||
      typeof this.s == "undefined"
    ) {
      this.x = 50;
      this.y = 50;
      this.s = 100;
    }
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.cartridge.classList.add("panning");
    this.mouseDown = true;
  }

  panEndHandler(e) {
    this.cartridge.classList.remove("panning");
    this.x = this.deltaX;
    this.y = this.deltaY;
    this.mouseDown = false;
  }

  panHandler(e) {
    if (!this.mouseDown) return;
    const deltaX = e.x - this.startX;
    const deltaY = e.y - this.startY;
    const deltaXPercentage = (deltaX / this.cartridgeWidth) * 100;
    const deltaYPercentage = (deltaY / this.cartridgeHeight) * 100;
    this.deltaX = Math.min(Math.max(this.x - deltaXPercentage * (this.s / 100), 0), 100);
    this.deltaY = Math.min(Math.max(this.y - deltaYPercentage * (this.s / 100), 0), 100);
    this.cartridge.style.backgroundPosition = `${this.deltaX}% ${this.deltaY}%`;
  }

  pointerDownHandler(e) {
    this.eventCache.push(e);
    if (this.eventCache.length === 1) {
      this.panStartHandler(e);
    } else {
      this.panEndHandler(e);
    }
  }

  pointerMoveHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache[index] = e;

    if (this.eventCache.length === 2) {
      const currentDiff = Math.abs(this.eventCache[0].clientX - this.eventCache[1].clientX);

      if (this.previousDiff > 0) {
        const delta = this.previousDiff - currentDiff;
        this.setZoom(delta);
      }

      this.previousDiff = currentDiff;
    } else {
      this.panHandler(e);
    }
  }

  pointerUpHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache.splice(index, 1);
    if (this.eventCache.length < 2) {
      this.previousDiff = -1;
    }
    if (this.eventCache.length <= 0) {
      this.x = this.deltaX;
      this.y = this.deltaY;
      this.mouseDown = false;
    }
  }

  loadImage() {
    this.cartridge.style.backgroundImage = `url(${this.cartridge.dataset.zoomImage})`;
  }
}

customElements.define("media-zoom", MediaZoom);
class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      loop: true,
    };

    this.defaultThumbsOptions = {
      loop: false,
      threshold: 10,
      spaceBetween: 16,
      slidesPerView: 6,
      watchSlidesProgress: true,
      breakpoints: {
        861: {
          slidesPerView: 4,
        },
      },
    };

    this.base_events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.setElements();
    this.events = [...this.base_events];
    if (document.readyState === "loading") {
      this.events.push({
        el: document,
        type: "DOMContentLoaded",
        callback: this.init.bind(this),
      });
      handleEventListeners(this.events, "add");
    } else {
      handleEventListeners(this.events, "add");
      this.init();
    }
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.swiper?.destroy();
    this.thumbsSwiper?.destroy();
    this.swiper = null;
    this.thumbsSwiper = null;
  }

  setElements() {
    this.elements = {
      swiper: this.querySelector(".media-gallery"),
      pagination: this.querySelector(".media-gallery__pagination"),
      prevButton: this.querySelector(".media-gallery__button-prev"),
      nextButton: this.querySelector(".media-gallery__button-next"),
      thumbs: this.querySelector(".media-gallery__thumbnails"),
    };

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.galleryOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.galleryOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.pagination) {
      this.swiperOptions.pagination = {
        el: this.elements.pagination,
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 5,
      };
    }

    if (this.elements.thumbs) {
      this.thumbsSwiperOptions = {
        ...this.defaultThumbsOptions,
      };
      if (this.elements.thumbs.dataset.galleryOptions) {
        this.thumbsSwiperOptions = {
          ...this.defaultThumbsOptions,
          ...JSON.parse(this.elements.thumbs.dataset.galleryOptions),
        };
      }
      this.swiperOptions.thumbs = {
        swiper: this.elements.thumbs,
      };
    }
    this.mediaZoom = this.querySelector("media-zoom");
  }

  clickHandler(e) {
    if (e.target.classList.contains("media-gallery__play")) {
      this.videoSlide = e.target.closest(".media-gallery__slide-video");
      this.videoSlideMedia = this.videoSlide.querySelector("video");
      this.featuredVideo = document.querySelector("featured-video");
      if (this.videoSlideMedia) {
        this.playMedia(e);
      } else if (this.featuredVideo) {
        this.scrollToVideo();
      }
    }
  }

  init() {
    if (this.swiper) return;
    if (!this.elements.swiper) return;
    if (this.elements.thumbs) {
      this.thumbsSwiper = new Swiper(this.elements.thumbs, this.thumbsSwiperOptions);
      this.swiperOptions.thumbs.swiper = this.thumbsSwiper;
    }

    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);

    this.swiper.on("slideChange", () => {
      const activeSlide = this.swiper.slides[this.swiper.activeIndex];
      if (this.activeVideo) {
        this.activeVideo.pause();
        this.activeVideo.currentTime = 0;
        this.videoSlide.classList.remove("video-playing");
      }
      if (!this.mediaZoom) return;
      if (activeSlide.querySelector(".media-gallery__slide-video")) {
        this.mediaZoom.classList.add("hide-zoom");
      } else {
        this.mediaZoom.classList.remove("hide-zoom");
      }
      const slideMediaURL = new URL(activeSlide.querySelector(".responsive-image img").src);
      slideMediaURL.searchParams.set("width", 2560);

      this.mediaZoom.querySelector("[data-zoom-image]").dataset.zoomImage = slideMediaURL;
    });
  }

  playMedia() {
    this.videoSlide.classList.add("video-playing");
    this.activeVideo = this.videoSlideMedia;
    this.videoSlideMedia.play();
  }
  scrollToVideo() {
    const topBar = document.querySelector(".announcement-bar");
    const header = document.querySelector(".site-header");
    this.headerHeight = header?.offsetHeight + topBar?.offsetHeight;
    window.scrollTo({
      top: this.featuredVideo.offsetTop - this.headerHeight,
      behavior: "smooth",
    });
  }
}
customElements.define("media-gallery", MediaGallery);

class StickyProductForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setElements();
    this.initIntersectionObserver();
    this.initMutationObserver();
    handleEventListeners(this.events, "add");
    this.updateContent();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutationObserver?.disconnect();
    handleEventListeners(this.events, "remove");
  }

  setElements() {
    this.main_product_form = document.querySelector(".main-product__form");
    this.add_to_cart_button = this.querySelector(".sticky-product-form__add");
    this.edit_button = this.querySelector(".sticky-product-form__edit");
    this.mediaQuery = window.matchMedia("(min-width: 768px)");

    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.mediaQuery,
        type: "change",
        callback: this.mediaQueryHandler.bind(this),
      },
    ];
  }

  clickHandler(e) {
    if (e.target === this.add_to_cart_button) {
      const form_button = this.main_product_form.querySelector(".product-form__button button");
      if (!form_button) return;
      form_button.click();
    }
    if (e.target === this.edit_button) {
      if (!this.main_product_form) return;
      const header =
        document.querySelector(".shopify-section-header-sticky") ||
        document.querySelector("header");
      const offset = header ? header.offsetHeight : 0;
      window.scrollTo({
        top: this.main_product_form.getBoundingClientRect().top + window.scrollY - offset - 16,
        behavior: "smooth",
      });
    }
  }

  mediaQueryHandler(e) {
    if (!e.matches) this.classList.remove("sticky-product-form--visible");
  }
  initIntersectionObserver() {
    if (!this.main_product_form) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!this.mediaQuery.matches) return;
        this.classList.toggle("sticky-product-form--visible", !entry.isIntersecting);
      },
      { threshold: 0 },
    );
    this.observer.observe(this.main_product_form);
  }

  initMutationObserver() {
    const productForm = document.querySelector("[data-product-form]");
    if (!productForm) return;
    if (!this.mediaQuery.matches) return;
    this.mutationObserver = new MutationObserver(() => this.updateContent());
    this.mutationObserver.observe(productForm, { childList: true, subtree: true });
  }

  updateContent() {
    this.updateOptions();
    this.updateDate();
    this.updateButton();
  }

  updateOptions() {
    const options = Array.from(document.querySelectorAll(".variant-select__radio:checked"))
      .filter(
        (input) => input.closest(".variant-select__group")?.dataset.optionTitle !== "delivery-type",
      )
      .map((input) => input.value);
    const el = this.querySelector(".sticky-product-form__options");
    if (el) el.textContent = options.join(" · ");
  }

  updateDate() {
    const dateInput = document.querySelector(".delivery-date-picker__input");
    const el = this.querySelector(".sticky-product-form__date");
    if (!el) return;
    if (dateInput?.value) {
      el.textContent = `Delivery date: ${dateInput.value}`;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  updateButton() {
    const form_button = this.main_product_form.querySelector(".product-form__button button");
    if (!form_button || !this.add_to_cart_button) return;
    this.add_to_cart_button.textContent = form_button.textContent.trim();
    this.add_to_cart_button.disabled = form_button.disabled;
    this.add_to_cart_button.classList.toggle(
      "btn-style__nostock",
      form_button.classList.contains("btn-style__nostock"),
    );
  }
}

customElements.define("sticky-product-form", StickyProductForm);
